import { BusinessCategory } from './../schemas/business_category';
import { PaymentProvider } from './../schemas/payment_provider';
import { inject, Injectable, signal } from '@angular/core';
import { readItems, createItem, updateItem, deleteItem } from '@directus/sdk';
import { directusAuthInstance } from '../libs/directus';
import { Business } from '../schemas/business';
import { BusinessAddress } from '../schemas/business_address';
import { Preference } from '../schemas/user_preference';
import { HandleErrorsService } from '../utils/services/handle-errors.service';
import { RequestsService } from '../utils/services/requests.service';
import { TranslocoService } from '@jsverse/transloco';
import { UserType } from '../schemas/user_type';
import { Locale } from '../schemas/locale';
import { PaymentAccount } from '../schemas/payment_account';
import { user } from '@angular/fire/auth';
import { UserService } from './user.service';
import { BusinessContact } from '../schemas/business_contact';
import { Transaction, TransactionStatus } from '../schemas/transaction';

@Injectable({
  providedIn: 'root',
})
export class BusinessService {
  private handleErrorsService = inject(HandleErrorsService);
  private requestsService = inject(RequestsService);
  private userService = inject(UserService);
  private transloco = inject(TranslocoService);
  directusClient = directusAuthInstance;

  // public businesses = signal<Business[] | null>(null);
  public paymentProviders = signal<PaymentProvider[]>([]);
  public businessCategories = signal<BusinessCategory[]>([]);
  public businessCategory = signal<BusinessCategory | null>(null);
  public business = signal<Business | null>(null);
  public businessPaymentAccounts = signal<PaymentAccount[]>([]);
  public transaction = signal<Transaction | null>(null);
  public businessDataLoaded = signal<boolean>(false);
  public loading = signal<boolean>(false);
  public address = signal<BusinessAddress | null>(null);
  public contacts = signal<BusinessContact[]>([]);
  public preferences = signal<Preference | null>(null);
  public language = this.transloco.getActiveLang();

  constructor() {}

  // async init() {
  //   this.loading.set(true);
  //   const loginState = localStorage.getItem('isLoggedIn') === 'true';
  //   if (loginState) {
  //     const business = await this.getUserBusinesses();
  //     await this.getBusinessPaymentAccounts(false);
  //     this.loading.set(false);
  //     this.businessDataLoaded.set(true);
  //     return business;
  //   } else {
  //     this.loading.set(false);
  //     return null;
  //   }
  // }

  public async getBusiness(businessCode: string | null) {
    if (!businessCode) return;
    if (!this.loading()) this.loading.set(true);
    const language = this.transloco.getActiveLang();
    try {
      const businesses = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('business', {
            fields: [
              '*',
              { logo: ['id', 'title', 'filename_disk', 'filename_disk'] },
              { banner: ['id', 'title', 'filename_disk', 'filename_disk'] },
              {
                country: [
                  'id',
                  'code',
                  'name',
                  'phone_code',
                  'currency',
                  'starts_with',
                ],
              },
              {
                categories: [{ category_id: ['*', { translations: ['*'] }] }],
              },
              {
                business_addresses: [{ city: ['*'] }, 'street_address'],
              },
              {
                business_contacts: ['*'],
              },
            ],
            deep: {
              categories: {
                category_id: {
                  translations: {
                    _filter: {
                      _and: [
                        {
                          languages_code: { _eq: language },
                        },
                      ],
                    },
                  },
                },
              },
            },
            limit: 1,
            filter: {
              code: {
                _eq: businessCode,
              },
            },
          }),
        ),
      );

      if (businesses.length === 0) {
        const businessData = await this.prepareBusinessData(null);
        this.loading.set(false);
        return businessData;
      }

      const businessData = await this.prepareBusinessData(businesses || null);
      this.businessDataLoaded.set(true);
      await this.getBusinessPaymentAccounts(false);
      this.loading.set(false);
      return businessData;
    } catch (error) {
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return await this.prepareBusinessData(null);
    }
  }

  async prepareBusinessData(businesses: any[] | null) {
    if (businesses === null) return;

    const businessesData: Business[] = [];
    businesses.forEach((business: Business) => {
      const businessData: Business = {
        id: business.id,
        name: business.name,
        store_name: business.store_name,
        logo: business.logo,
        banner: business.banner,
        code: business.code,
        caption: business.caption,
        description: business.description,
        country: business.country,
        wifi_name: business.wifi_name,
        wifi_password: business.wifi_password,
      };

      const business_contacts: any[] = business?.business_contacts || [];
      const business_addresses: any[] = business?.business_addresses || [];
      const categories: any[] = business?.categories || [];
      const category = categories[0]?.category_id || null;

      if (business_contacts.length > 0) {
        this.contacts.set(business_contacts);
      }

      if (categories.length > 0 && category) {
        const categoryData: BusinessCategory = {
          id: category.id,
          business_category_id: categories[0].id,
          name: category.translations![0]?.name || category.name,
        };

        this.businessCategory.set(categoryData);
        businessData.category = categoryData || null;
      }

      if (business_addresses.length > 0) {
        this.address.set(business_addresses[0] as BusinessAddress);
      }

      businessesData.push(businessData);
    });

    // this.businesses.set(businessesData);
    this.business.set(businessesData[0]);
    // console.log('Business', this.address());
    return businessesData[0];
  }

  async getPaymentProviders(getCache: boolean = true) {
    if (this.paymentProviders()?.length > 0 && getCache) {
      return this.paymentProviders();
    }
    const language = this.transloco.getActiveLang();
    // this.loading.set(true);
    // console.log('Payment Providers', this.paymentProviders());
    // console.log('Business', this.business());
    // await new Promise((resolve) => setTimeout(resolve, 3000));
    // TODO: singe I have this data, I can decide to change the User Experience later to have two steps one for choosing the Account type that is aggregated from the response the show providers based on the account type or just show all the providers
    try {
      const paymentProviders: PaymentProvider[] = [];
      const paymentProviderResponse: PaymentProvider[] =
        await this.requestsService.handleRequest(() =>
          this.directusClient.request(
            readItems('payment_providers', {
              filter: {
                _and: [
                  {
                    is_active: {
                      _eq: true,
                    },
                  },
                  {
                    user_type: {
                      code: {
                        _eq: UserType.BUSINESS,
                      },
                    },
                  },
                  {
                    country: {
                      code: {
                        _eq: (this.business()!.country as Locale).code,
                      },
                    },
                  },
                ],
              },
              sort: 'sort',
              limit: -1,
              deep: {
                translations: {
                  _filter: {
                    _and: [
                      {
                        languages_code: { _eq: language },
                      },
                    ],
                  },
                },
              },
              // fields: ['id', 'name', { translations: ['*'] }],
              fields: [
                'id',
                'name',
                'code',
                'display_name',
                'icon',
                { account_type: ['*'] },
                { translations: ['*'] },
              ],
            }),
          ),
        );

      paymentProviderResponse.forEach((provider) => {
        const providerData: PaymentProvider = {
          id: provider.id,
          name: provider.translations![0]?.name || provider.name,
          display_name:
            provider.translations![0]?.display_name || provider.display_name,
          code: provider.code,
          icon: provider.icon,
          account_type: provider.account_type,
        };

        paymentProviders.push(providerData);
      });

      this.paymentProviders.set(paymentProviders);

      // this.loading.set(false);
      return paymentProviders || [];
    } catch (error) {
      // this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async getBusinessPaymentAccounts(getCache: boolean = true) {
    // console.trace('Getting Business Payment Methods');

    if (this.businessPaymentAccounts().length > 0 && getCache) {
      return this.businessPaymentAccounts();
    }

    // await new Promise((resolve) => setTimeout(resolve, 3000));

    const language = this.transloco.getActiveLang();
    this.loading.set(true);
    try {
      const accounts: PaymentAccount[] = [];
      const accountsResponse: PaymentAccount[] =
        await this.requestsService.handleRequest(() =>
          this.directusClient.request(
            readItems('payment_accounts', {
              filter: {
                business: {
                  _eq: this.business()!.id,
                },
                is_active: {
                  _eq: true,
                },
                // user: {
                //   _eq: '9b99eab7-9dfc-4928-97d3-da8ef22e26dc',
                // },
              },
              deep: {
                payment_provider: {
                  translations: {
                    _filter: {
                      _and: [
                        {
                          languages_code: { _eq: language },
                        },
                      ],
                    },
                  },
                  ussds_from: {
                    _filter: {
                      _and: [
                        {
                          is_active: { _eq: true },
                        },
                      ],
                    },
                    service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                    sub_service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                  },
                  ussds_to: {
                    _filter: {
                      _and: [
                        {
                          is_active: { _eq: true },
                        },
                      ],
                    },
                    service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                    sub_service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                  },
                },
                // business: {
                //   _filter: {
                //     _and: [
                //       {
                //         code: {
                //           _eq: this.business()!.code,
                //         },
                //       },
                //     ],
                //   },
                // },
              },
              fields: [
                'id',
                'name',
                'number',
                'number_display',
                'code',
                'label',
                {
                  type: ['code'],
                },
                // 'user.id',
                // 'user.names',
                // 'user.code',
                // 'business.id',
                // 'business.code',
                // 'business.name',
                {
                  payment_provider: [
                    'id',
                    'code',
                    'icon',
                    'name',
                    { translations: ['*'] },
                    {
                      ussds_from: [
                        '*',
                        { from_account_type: ['code'] },
                        { from_provider: ['code'] },
                        { to_provider: ['code'] },
                        { route: ['code'] },
                        { sub_route: ['code'] },
                        {
                          service: ['code', { translations: ['display_name'] }],
                        },
                        {
                          sub_service: [
                            'code',
                            { translations: ['display_name'] },
                          ],
                        },
                      ],
                    },
                    {
                      ussds_to: [
                        '*',
                        { from_account_type: ['code'] },
                        { from_provider: ['code'] },
                        { to_provider: ['code'] },
                        { route: ['code'] },
                        { sub_route: ['code'] },
                        {
                          service: ['code', { translations: ['display_name'] }],
                        },
                        {
                          sub_service: [
                            'code',
                            { translations: ['display_name'] },
                          ],
                        },
                      ],
                    },
                    { networks: [{ network_id: ['*'] }] },
                  ],
                },
              ],
            }),
          ),
        );

      accountsResponse.forEach((account) => {
        const providerData: PaymentProvider = {
          id: account.payment_provider?.id || '',
          name:
            account.payment_provider?.translations![0]?.name ||
            account.payment_provider?.name,
          display_name:
            account.payment_provider?.translations![0]?.display_name ||
            account.payment_provider?.display_name,
          code: account.payment_provider?.code,
          icon: account.payment_provider?.icon,
          ussds_from: account.payment_provider?.ussds_from,
          ussds_to: account.payment_provider?.ussds_to,
          networkCode:
            account.payment_provider?.networks![0]?.network_id?.network_code,
          networkName:
            account.payment_provider?.networks![0]?.network_id?.display_name,
        };
        const accountData: PaymentAccount = {
          id: account.id,
          name: account.name,
          number: account.number,
          number_display: account.number_display,
          label: account.label,
          type: account.type,
          code: account.code,
          payment_provider: providerData,
          country: account.country,
        };

        if (providerData.ussds_to && providerData.ussds_to?.length > 0) {
          accounts.push(accountData);
        }
      });

      this.businessPaymentAccounts.set(accounts);

      this.loading.set(false);
      return accounts || [];
    } catch (error) {
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async getBusinessPaymentAccount(accountCode: string) {
    // console.trace('Getting Business Payment Methods');

    // await new Promise((resolve) => setTimeout(resolve, 3000));

    const language = this.transloco.getActiveLang();
    this.loading.set(true);
    try {
      const accounts: PaymentAccount[] = [];
      const accountsResponse: PaymentAccount[] =
        await this.requestsService.handleRequest(() =>
          this.directusClient.request(
            readItems('payment_accounts', {
              filter: {
                code: {
                  _eq: accountCode,
                },
                is_active: {
                  _eq: true,
                },
              },
              deep: {
                payment_provider: {
                  translations: {
                    _filter: {
                      _and: [
                        {
                          languages_code: { _eq: language },
                        },
                      ],
                    },
                  },
                  ussds_from: {
                    _filter: {
                      _and: [
                        {
                          is_active: { _eq: true },
                        },
                      ],
                    },
                    service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                    sub_service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                  },
                  ussds_to: {
                    _filter: {
                      _and: [
                        {
                          is_active: { _eq: true },
                        },
                      ],
                    },
                    service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                    sub_service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                  },
                },
                // business: {
                //   _filter: {
                //     _and: [
                //       {
                //         code: {
                //           _eq: this.business()!.code,
                //         },
                //       },
                //     ],
                //   },
                // },
              },
              fields: [
                'id',
                'name',
                'number',
                'number_display',
                'code',
                'label',
                {
                  type: ['code'],
                },
                // 'user.id',
                // 'user.names',
                // 'user.code',
                // 'business.id',
                // 'business.code',
                // 'business.name',
                {
                  payment_provider: [
                    'id',
                    'code',
                    'icon',
                    'name',
                    { translations: ['*'] },
                    {
                      ussds_from: [
                        '*',
                        { from_account_type: ['code'] },
                        { from_provider: ['code'] },
                        { to_provider: ['code'] },
                        { route: ['code'] },
                        { sub_route: ['code'] },
                        {
                          service: ['code', { translations: ['display_name'] }],
                        },
                        {
                          sub_service: [
                            'code',
                            { translations: ['display_name'] },
                          ],
                        },
                      ],
                    },
                    {
                      ussds_to: [
                        '*',
                        { from_account_type: ['code'] },
                        { from_provider: ['code'] },
                        { to_provider: ['code'] },
                        { route: ['code'] },
                        { sub_route: ['code'] },
                        {
                          service: ['code', { translations: ['display_name'] }],
                        },
                        {
                          sub_service: [
                            'code',
                            { translations: ['display_name'] },
                          ],
                        },
                      ],
                    },
                    { networks: [{ network_id: ['*'] }] },
                  ],
                },
                {
                  business: [
                    '*',
                    { logo: ['id', 'title', 'filename_disk', 'filename_disk'] },
                    {
                      banner: ['id', 'title', 'filename_disk', 'filename_disk'],
                    },
                    {
                      country: [
                        'id',
                        'code',
                        'name',
                        'phone_code',
                        'currency',
                        'starts_with',
                      ],
                    },
                    {
                      categories: [
                        { category_id: ['*', { translations: ['*'] }] },
                      ],
                    },
                    {
                      business_addresses: [{ city: ['*'] }, 'street_address'],
                    },
                    {
                      business_contacts: ['*'],
                    },
                  ],
                },
              ],
            }),
          ),
        );

      accountsResponse.forEach(async (account) => {
        const providerData: PaymentProvider = {
          id: account.payment_provider?.id || '',
          name:
            account.payment_provider?.translations![0]?.name ||
            account.payment_provider?.name,
          display_name:
            account.payment_provider?.translations![0]?.display_name ||
            account.payment_provider?.display_name,
          code: account.payment_provider?.code,
          icon: account.payment_provider?.icon,
          ussds_from: account.payment_provider?.ussds_from,
          ussds_to: account.payment_provider?.ussds_to,
          networkCode:
            account.payment_provider?.networks![0]?.network_id?.network_code,
          networkName:
            account.payment_provider?.networks![0]?.network_id?.display_name,
        };
        const accountData: PaymentAccount = {
          id: account.id,
          name: account.name,
          number: account.number,
          number_display: account.number_display,
          label: account.label,
          type: account.type,
          code: account.code,
          payment_provider: providerData,
          country: account.country,
        };

        accounts.push(accountData);
      });
      const business = accounts[0].business as Business;
      await this.prepareBusinessData([business]);
      this.businessDataLoaded.set(true);
      this.businessPaymentAccounts.set(accounts);

      this.loading.set(false);
      return accounts || [];
    } catch (error) {
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async getBusinessTransaction(code: string): Promise<Transaction | null> {
    const language = this.transloco.getActiveLang();
    try {
      const transactions = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('transactions', {
            deep: {
              receiver_account: {
                payment_provider: {
                  translations: {
                    _filter: {
                      _and: [
                        {
                          languages_code: { _eq: language },
                        },
                      ],
                    },
                  },
                  ussds_from: {
                    _filter: {
                      _and: [
                        {
                          is_active: { _eq: true },
                        },
                      ],
                    },
                    service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                    sub_service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                  },
                  ussds_to: {
                    _filter: {
                      _and: [
                        {
                          is_active: { _eq: true },
                        },
                      ],
                    },
                    service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                    sub_service: {
                      _filter: {
                        _and: [
                          {
                            code: { _contains: 'SEND_MONEY' },
                          },
                        ],
                      },
                      translations: {
                        _filter: {
                          _and: [
                            {
                              languages_code: { _eq: language },
                            },
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
            fields: [
              'id',
              'type',
              'code',
              'status',
              'amount',
              'customer_number',
              'customer_nickname',
              {
                receiver_account: [
                  'id',
                  'name',
                  'number',
                  'number_display',
                  'code',
                  'label',
                  'sim_slot',
                  {
                    type: ['code'],
                  },
                  // 'user.id',
                  // 'user.names',
                  // 'user.code',
                  // 'business.id',
                  // 'business.code',
                  // 'business.name',
                  {
                    payment_provider: [
                      'id',
                      'code',
                      'icon',
                      'name',
                      { translations: ['*'] },
                      {
                        ussds_from: [
                          '*',
                          { from_account_type: ['code'] },
                          { from_provider: ['code'] },
                          { to_provider: ['code'] },
                          { route: ['code'] },
                          { sub_route: ['code'] },
                          {
                            service: [
                              'code',
                              { translations: ['display_name'] },
                            ],
                          },
                          {
                            sub_service: [
                              'code',
                              { translations: ['display_name'] },
                            ],
                          },
                        ],
                      },
                      {
                        ussds_to: [
                          '*',
                          { from_account_type: ['code'] },
                          { from_provider: ['code'] },
                          { to_provider: ['code'] },
                          { route: ['code'] },
                          { sub_route: ['code'] },
                          {
                            service: [
                              'code',
                              { translations: ['display_name'] },
                            ],
                          },
                          {
                            sub_service: [
                              'code',
                              { translations: ['display_name'] },
                            ],
                          },
                        ],
                      },
                      { networks: [{ network_id: ['*'] }] },
                    ],
                  },
                ],
              },
              {
                business_receiver: [
                  '*',
                  { logo: ['id', 'title', 'filename_disk', 'filename_disk'] },
                  {
                    banner: ['id', 'title', 'filename_disk', 'filename_disk'],
                  },
                  {
                    country: [
                      'id',
                      'code',
                      'name',
                      'phone_code',
                      'currency',
                      'starts_with',
                    ],
                  },
                  {
                    categories: [
                      { category_id: ['*', { translations: ['*'] }] },
                    ],
                  },
                  {
                    business_addresses: [{ city: ['*'] }, 'street_address'],
                  },
                  {
                    business_contacts: ['*'],
                  },
                ],
              },
            ],
            filter: {
              _and: [
                {
                  code: { _eq: code },
                },
              ],
            },
            limit: 1,
          }),
        ),
      );

      if (transactions.length === 0) {
        return null;
      }
      const transaction = (transactions as Transaction[]).find(
        (transaction) => transaction.code === code,
      );

      const business = transaction?.business_receiver as Business;
      const receiver_account =
        transaction?.receiver_account as PaymentAccount | null;
      if (receiver_account) {
        const provider = (transaction?.receiver_account as PaymentAccount)
          .payment_provider as PaymentProvider;
        const providerData: PaymentProvider = {
          id: provider?.id || '',
          name: provider?.translations![0]?.name || provider?.name,
          display_name:
            provider?.translations![0]?.display_name || provider?.display_name,
          code: provider?.code,
          icon: provider?.icon,
          ussds_from: provider?.ussds_from,
          ussds_to: provider?.ussds_to,
          networkCode: provider?.networks![0]?.network_id?.network_code,
          networkName: provider?.networks![0]?.network_id?.display_name,
        };
        const accountData: PaymentAccount = {
          id: receiver_account?.id,
          name: receiver_account?.name,
          number: receiver_account?.number,
          number_display: receiver_account?.number_display,
          label: receiver_account?.label,
          type: receiver_account?.type,
          code: receiver_account?.code,
          payment_provider: providerData,
          country: receiver_account?.country,
        };
        await this.prepareBusinessData([business]);
        this.businessDataLoaded.set(true);
        this.businessPaymentAccounts.set([accountData]);
      } else {
        await this.prepareBusinessData([business]);
        this.businessDataLoaded.set(true);
        this.getBusinessPaymentAccounts(false);
      }

      this.transaction.set(transaction || null);

      // console.log('Address', this.address());
      // console.log('Business', this.business());
      // console.log('Business Payment Accounts', this.businessPaymentAccounts());
      // console.log('Transaction', this.transaction());
      return transaction || null;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }
}
